// Modal para verificar el código de 6 dígitos enviado por mail a un email
// nuevo agregado a la lista de destinatarios de informes.
//
// Flujo: tc_qml genera un código y lo envía por Brevo al destinatario. El
// destinatario (o el dueño, si tiene acceso al mail) lee el código y lo
// ingresa acá. Endpoint: POST /monitor/reports-verify-code.

import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { auth } from "../../firebase";

const TCSOCKET_URL =
  process.env.REACT_APP_TCSOCKET_URL || "https://api.ticketcontrol.net";

const VerifyEmailModal = ({ isOpen, email, machineKey, onClose, onVerified }) => {
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCode("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const verify = async () => {
    const clean = (code || "").trim();
    if (!/^\d{6}$/.test(clean)) {
      toast.error("El código debe ser de 6 dígitos numéricos.");
      return;
    }
    setSending(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Sesión expirada. Volvé a iniciar sesión.");
        setSending(false);
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(`${TCSOCKET_URL}/monitor/reports-verify-code`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: machineKey,
          payload: { email, code: clean },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.verified) {
        toast.error(data.message || "Código incorrecto. Probá de nuevo.");
        setSending(false);
        return;
      }
      toast.success("Email verificado");
      onVerified && onVerified(email);
      onClose && onClose();
    } catch (err) {
      toast.error(`Error de red: ${err.message || err}`);
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      const res = await fetch(`${TCSOCKET_URL}/monitor/reports-emails-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: machineKey,
          payload: { action: "resend", email },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "No se pudo reenviar el código.");
        return;
      }
      toast.info("Código reenviado");
    } catch (err) {
      toast.error(`Error de red: ${err.message || err}`);
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Verificar email</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Se envió un código de 6 dígitos a{" "}
          <b>{email}</b>. Pedile al destinatario que te pase el código y
          ingresalo abajo para confirmar.
        </p>
        <Input
          innerRef={inputRef}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => e.key === "Enter" && verify()}
          placeholder="000000"
          style={{
            textAlign: "center",
            fontSize: 24,
            letterSpacing: 8,
            fontWeight: 600,
          }}
          disabled={sending}
        />
        <div className="text-center small mt-3">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={resend}
            disabled={resending || sending}
          >
            {resending ? "Reenviando..." : "Reenviar código"}
          </button>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={sending}>
          Cancelar
        </Button>
        <Button color="primary" onClick={verify} disabled={sending || code.length !== 6}>
          {sending ? <Spinner size="sm" /> : "Verificar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default VerifyEmailModal;
